import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { FaReceipt, FaLayerGroup, FaExclamationTriangle } from 'react-icons/fa';
import IndicadoresMenores from './IndicadoresMenores';
import IndicadoresSeace from './IndicadoresSeace';
import GraficoBarrasMenores from './GraficoBarrasMenores';
import GraficoBarrasSeace from './GraficoBarrasSeace';
import LineaTiempoMenores from './LineaTiempoMenores';
import SeguimientoEntidades from './SeguimientoEntidades';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

// Normaliza una fecha de publicación (varios formatos) a 'YYYY-MM-DD'
const parseFechaADia = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (s === '' || s === '-') return null;

  // ISO: 2024-01-15 o 2024-01-15T10:30:00
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;

  // DD/MM/YYYY o DD/MM/YYYY HH:MM
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;

  // DD-MM-YYYY
  m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;

  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
  }
  return null;
};

const Principal = ({ user, onToggleSidebar }) => {
  const [dataContratosMenores, setDataContratosMenores] = useState({
    indicadores: null,
    graficoDepartamentos: null
  });
  const [dataSeace, setDataSeace] = useState({
    indicadores: null,
    graficoDepartamentos: null
  });
  const [timelineMenores, setTimelineMenores] = useState(null);
  const [trackingEntidades, setTrackingEntidades] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Cargando datos del dashboard desde Supabase...');

        const { data: indicadoresMenoresDB, error: errorIndicadoresDB } = await supabase
          .from('indicadores_dashboard')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        let menoresIndicadores;

        if (!errorIndicadoresDB && indicadoresMenoresDB) {
          console.log('✅ Usando indicadores pre-calculados de indicadores_dashboard');
          menoresIndicadores = {
            menores_total: indicadoresMenoresDB.menores_total || 0,
            menores_entidad: indicadoresMenoresDB.menores_entidad || 0,
            menores_nuevos: indicadoresMenoresDB.menores_nuevos || 0,
            menores_vigentes: indicadoresMenoresDB.menores_vigentes || 0,
            menores_proximas: indicadoresMenoresDB.menores_proximas || 0
          };
        } else {
          console.log('⚠️ No hay datos en indicadores_dashboard, calculando desde contratos_menores...');
          
          const { count: totalMenores, error: errorTotal } = await supabase
            .from('contratos_menores')
            .select('*', { count: 'exact', head: true });

          if (errorTotal) throw errorTotal;

          const { data: entidadesData, error: errorEntidades } = await supabase
            .from('contratos_menores')
            .select('entidad')
            .not('entidad', 'is', null);

          if (errorEntidades) throw errorEntidades;

          const entidadesUnicasMenores = new Set(entidadesData?.map(e => e.entidad) || []).size;

          const { count: nuevosMenores, error: errorNuevos } = await supabase
            .from('contratos_menores')
            .select('*', { count: 'exact', head: true })
            .eq('convocatorias_hoy', 1);

          if (errorNuevos) throw errorNuevos;

          const { count: vigentesMenores, error: errorVigentes } = await supabase
            .from('contratos_menores')
            .select('*', { count: 'exact', head: true })
            .eq('estado', 'Vigente');

          if (errorVigentes) throw errorVigentes;

          const { count: proximosVencerMenores, error: errorProximos } = await supabase
            .from('contratos_menores')
            .select('*', { count: 'exact', head: true })
            .eq('nomestadocotiza', 'Próximo a Vencer');

          const proximas = errorProximos ? 0 : (proximosVencerMenores || 0);

          menoresIndicadores = {
            menores_total: totalMenores || 0,
            menores_entidad: entidadesUnicasMenores || 0,
            menores_nuevos: nuevosMenores || 0,
            menores_vigentes: vigentesMenores || 0,
            menores_proximas: proximas
          };
        }

        console.log('✅ Indicadores Contratos Menores:', menoresIndicadores);

        const { data: menoresPorDpto, error: errorGraficoMenores } = await supabase
          .from('contratos_menores')
          .select('departamento')
          .gt('convocatorias_hoy', 0);

        if (errorGraficoMenores) throw errorGraficoMenores;

        const deptoCountMenores = {};
        menoresPorDpto?.forEach(item => {
          const dpto = item.departamento || 'Sin especificar';
          deptoCountMenores[dpto] = (deptoCountMenores[dpto] || 0) + 1;
        });

        const menoresGrafico = Object.entries(deptoCountMenores)
          .map(([departamento, cantidad]) => ({ departamento, cantidad }))
          .sort((a, b) => b.cantidad - a.cantidad);

        console.log('✅ Gráfico Contratos Menores:', menoresGrafico.length, 'departamentos');

        // Línea de tiempo: evolución de convocatorias (tabla histórica)
        let registrosHistorico = [];
        let fromHist = 0;
        const pageSizeHist = 1000;
        let hayMasHist = true;

        while (hayMasHist) {
          const { data: pageHist, error: errorHist } = await supabase
            .from('seguimiento_convocatorias_historico')
            .select('FECHA_PUBLICACION, CANTIDAD')
            .not('FECHA_PUBLICACION', 'is', null)
            .range(fromHist, fromHist + pageSizeHist - 1);

          if (errorHist) throw errorHist;

          if (pageHist && pageHist.length > 0) {
            registrosHistorico = registrosHistorico.concat(pageHist);
            fromHist += pageSizeHist;
            hayMasHist = pageHist.length === pageSizeHist;
          } else {
            hayMasHist = false;
          }
        }

        // FECHA_PUBLICACION viene en formato dd/mm/yyyy; sumamos CANTIDAD por día
        const conteoPorDia = {};
        registrosHistorico.forEach((item) => {
          const dia = parseFechaADia(item.FECHA_PUBLICACION);
          const cantidad = Number(item.CANTIDAD) || 0;
          if (dia) conteoPorDia[dia] = (conteoPorDia[dia] || 0) + cantidad;
        });

        const menoresTimeline = Object.entries(conteoPorDia)
          .map(([fecha, cantidad]) => ({ fecha, cantidad }))
          .sort((a, b) => a.fecha.localeCompare(b.fecha));

        console.log('✅ Línea de tiempo (histórico):', menoresTimeline.length, 'fechas');

        // Seguimiento de entidades (tabla tracking_entidades)
        let registrosTracking = [];
        let fromTrack = 0;
        const pageSizeTrack = 1000;
        let hayMasTrack = true;

        while (hayMasTrack) {
          const { data: pageTrack, error: errorTrack } = await supabase
            .from('tracking_entidades')
            .select('FECHA_PUBLICACION, ENTIDADES_DIA, ACUMULADO_ENTIDADES')
            .not('FECHA_PUBLICACION', 'is', null)
            .range(fromTrack, fromTrack + pageSizeTrack - 1);

          if (errorTrack) throw errorTrack;

          if (pageTrack && pageTrack.length > 0) {
            registrosTracking = registrosTracking.concat(pageTrack);
            fromTrack += pageSizeTrack;
            hayMasTrack = pageTrack.length === pageSizeTrack;
          } else {
            hayMasTrack = false;
          }
        }

        const trackingData = registrosTracking
          .map((item) => ({
            fecha: parseFechaADia(item.FECHA_PUBLICACION),
            entidadesDia: Number(item.ENTIDADES_DIA) || 0,
            acumulado: Number(item.ACUMULADO_ENTIDADES) || 0
          }))
          .filter((item) => item.fecha)
          .sort((a, b) => a.fecha.localeCompare(b.fecha));

        console.log('✅ Seguimiento de entidades:', trackingData.length, 'fechas');

        const { count: totalSeace, error: errorTotalSeace } = await supabase
          .from('seace_news')
          .select('*', { count: 'exact', head: true });

        if (errorTotalSeace) throw errorTotalSeace;

        const { data: entidadesSeaceData, error: errorEntidadesSeace } = await supabase
          .from('seace_news')
          .select('entidad')
          .not('entidad', 'is', null);

        if (errorEntidadesSeace) throw errorEntidadesSeace;

        const entidadesUnicasSeace = new Set(entidadesSeaceData?.map(e => e.entidad) || []).size;

        const seaceIndicadores = {
          totalRegistros: totalSeace || 0,
          entidadesUnicas: entidadesUnicasSeace || 0
        };

        console.log('✅ Indicadores SEACE:', seaceIndicadores);

        const { data: seacePorDpto, error: errorGraficoSeace } = await supabase
          .from('seace_news')
          .select('departamento');

        if (errorGraficoSeace) throw errorGraficoSeace;

        const deptoCountSeace = {};
        seacePorDpto?.forEach(item => {
          const dpto = item.departamento || 'Sin especificar';
          deptoCountSeace[dpto] = (deptoCountSeace[dpto] || 0) + 1;
        });

        const seaceGrafico = Object.entries(deptoCountSeace)
          .map(([departamento, cantidad]) => ({ departamento, cantidad }))
          .sort((a, b) => b.cantidad - a.cantidad);

        console.log('✅ Gráfico SEACE:', seaceGrafico.length, 'departamentos');

        setDataContratosMenores({
          indicadores: menoresIndicadores,
          graficoDepartamentos: menoresGrafico
        });

        setTimelineMenores(menoresTimeline);

        setTrackingEntidades(trackingData);

        setDataSeace({
          indicadores: seaceIndicadores,
          graficoDepartamentos: seaceGrafico
        });


        console.log('✅ Todos los datos cargados exitosamente');
        setLoading(false);

      } catch (error) {
        console.error('❌ Error cargando datos del dashboard:', error);
        setError(error.message || 'Error al cargar los datos');
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

const handleMobileMenuClick = (e) => {
  if (!isMobile || !onToggleSidebar) return;
  
  // Verificar si el click fue en un elemento interactivo
  const target = e.target;
  const isInteractiveElement = target.closest('button, a, input, select, textarea, [role="button"]');
  
  // Solo abrir el sidebar si NO es un elemento interactivo
  if (!isInteractiveElement) {
    console.log('📱 Tap detectado en móvil - Abriendo sidebar');
    onToggleSidebar(false); // false = abrir sidebar
  }
};
  if (error) {
    return (
      <div style={{
        padding: isMobile ? '1rem' : '2rem',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f4f6f8'
      }}>
        <div style={{
          background: '#fff',
          border: '1px solid #e8ebf0',
          borderRadius: 12,
          padding: isMobile ? '2rem 1.5rem' : '2.5rem',
          boxShadow: '0 6px 24px rgba(15,23,42,.06)',
          maxWidth: 460,
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: 56,
            height: 56,
            margin: '0 auto 1.25rem',
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FaExclamationTriangle size={24} color="#dc2626" />
          </div>
          <h2 style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#0f172a',
            marginBottom: '0.75rem'
          }}>
            Error al cargar datos
          </h2>
          <p style={{
            color: '#5b6779',
            marginBottom: '1.75rem',
            lineHeight: 1.6,
            fontSize: 13.5
          }}>
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 2rem',
              background: '#0f766e',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: 'pointer',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f4f6f8',
        cursor: isMobile ? 'pointer' : 'default'
      }}
      onClick={isMobile ? handleMobileMenuClick : undefined}
    >
      <div style={{
        maxWidth: 1400,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
        padding: isMobile ? '18px 16px 24px' : '26px 30px 30px'
      }}>

        {/* Cabecera */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 20,
          paddingBottom: 16,
          borderBottom: '1px solid #e2e6ee'
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.09em', color: '#0f766e' }}>DASHBOARD</div>
            <div style={{
              fontSize: isMobile ? 20 : 25,
              fontWeight: 600,
              color: '#0f172a',
              letterSpacing: '-0.02em',
              marginTop: 5
            }}>
              Información General
            </div>
          </div>
          {!isMobile && (
            <div style={{ fontSize: 13, color: '#5b6779', whiteSpace: 'nowrap' }}>
              Bienvenido, <span style={{ fontWeight: 600, color: '#0f172a' }}>{user?.name || 'Usuario'}</span>
            </div>
          )}
        </div>

        {/* Contenido */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 14 : 16,
          marginTop: isMobile ? 18 : 20
        }}>

          {/* Indicadores agrupados por tipo de contrato */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 3fr) minmax(0, 2fr)', gap: isMobile ? 18 : 14 }}>
            {[
              { title: 'Contratos Menores', Icon: FaReceipt, color: '#0f4f47', background: '#e8f2f0', columns: 3, Component: IndicadoresMenores, data: dataContratosMenores.indicadores },
              { title: 'Convocatorias del SEACE', Icon: FaLayerGroup, color: '#14507d', background: '#eff6fb', columns: 2, Component: IndicadoresSeace, data: dataSeace.indicadores }
            ].map(({ title, Icon, color, background, columns, Component, data }) => (
              <section key={title} aria-label={title} style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 8, background, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={14} color={color} />
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{title}</span>
                  {!isMobile && <span style={{ flex: 1, height: 1, background: '#e2e6ee' }} />}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile ? 2 : columns}, minmax(0, 1fr))`, gap: isMobile ? 12 : 14 }}>
                  <Component data={data} isLoading={loading} />
                </div>
              </section>
            ))}
          </div>

          {/* Nuevas convocatorias en el tiempo (Contratos Menores) */}
          <LineaTiempoMenores
            data={timelineMenores}
            isLoading={loading}
          />

          {/* Seguimiento de entidades */}
          <SeguimientoEntidades
            data={trackingEntidades}
            isLoading={loading}
          />

          {/* Análisis por departamento: Menores + Mayores */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: 16
          }}>
            <GraficoBarrasMenores
              data={dataContratosMenores.graficoDepartamentos}
              isLoading={loading}
            />
            <GraficoBarrasSeace
              data={dataSeace.graficoDepartamentos}
              isLoading={loading}
            />
          </div>

        </div>
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulseCard { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }
        @keyframes dvUp { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; } }
        @keyframes dvFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dvBar { from { transform: scaleX(0); } to { transform: scaleX(1); } }

        /* Tarjetas KPI: elevación al pasar el cursor */
        .dash-kpi { transition: box-shadow .18s ease, transform .18s ease; }
        .dash-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(15,23,42,.09); }

        /* Filas del ranking por departamento */
        .dash-rank-row:hover { background: #f7f9f9; }
        .dash-rank-row--azul:hover { background: #f7f9fb; }

        /* Segmentado Día / Semana / Mes */
        .dash-seg-btn:hover { background: rgba(255,255,255,.6); }
        .dash-seg-btn--activo:hover { background: #fff; }
      `}</style>
    </div>
  );
};

export default Principal;